import { useQuery } from '@apollo/client';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import * as Sentry from '@sentry/core';
import * as ScreenOrientation from 'expo-screen-orientation';
import chunk from 'lodash/chunk';
import sum from 'lodash/sum';
import { ComponentProps, useCallback } from 'react';
import { Platform, RefreshControl } from 'react-native';
import { SystemBars } from 'react-native-edge-to-edge';

import { graphql } from '@oui/lib/src/graphql/tada';

import { Icon } from '../components/Icon';
import { PracticeTile } from '../components/PracticeTile';
import { ScrollView } from '../components/ScrollView';
import { TabHeader } from '../components/TabHeader';
import { Text } from '../components/Text';
import { View } from '../components/View';
import { Environment, environment } from '../constants';
import { useOpenDeeplink } from '../hooks/useOpenDeeplink';
import { useI18n } from '../lib/i18n';
import { useTheme } from '../styles';

export const PracticeQuery = graphql(`
  query Practice {
    user {
      ID
      role {
        ID
        widgets {
          ID
          description
          name
          iconName
          locked
          practiceCount
          practiceTileColor
          practiceTileDeeplink
          unlockSession {
            session {
              sessionID
              ordinal
            }
          }
        }
      }
    }
  }
`);

export function Practice() {
  const isFocused = useIsFocused();
  const { $t } = useI18n();
  const { theme } = useTheme();
  const { data, loading, refetch } = useQuery(PracticeQuery);
  const openDeeplink = useOpenDeeplink();

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== 'web') {
        void ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
      }
      try {
        void refetch();
      } catch (e) {
        // HMR error in dev
        if (environment !== Environment.DEVELOPMENT) {
          Sentry.captureException(e);
        }
      }
      return () => {
        if (Platform.OS !== 'web') {
          void ScreenOrientation.unlockAsync();
        }
      };
    }, [refetch]),
  );

  const totalPracticeCount = sum(data?.user?.role?.widgets.map((w) => w.practiceCount) ?? []) ?? 0;

  return (
    <View style={{ flex: 1 }}>
      {isFocused ? <SystemBars style="dark" /> : null}
      <ScrollView
        refreshControl={
          <RefreshControl
            refreshing={!!loading}
            onRefresh={() => {
              void Promise.all([refetch()]);
            }}
            title={$t({ id: 'Practice_refreshIndicator', defaultMessage: 'Update' })}
            progressViewOffset={150}
          />
        }
        testID="Practice_scrollView"
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingBottom: 150,
        }}
      >
        <TabHeader heading={$t({ id: 'Practice_heading', defaultMessage: 'Practice' })} />
        <View
          style={{
            marginHorizontal: -20,
            backgroundColor: theme.color.dark,
            padding: 16,
            marginBottom: 20,
            justifyContent: 'center',
            gap: 12,
          }}
          row
        >
          <Icon name="practice" color="white" size={24} />
          <Text
            text={$t(
              {
                id: 'Practice_totalCompletedPractices',
                defaultMessage: '{count} total practices done',
              },
              { count: totalPracticeCount ?? 0 },
            )}
            weight="semibold"
            size={21}
            color="white"
          />
        </View>
        <View style={{ gap: 20 }}>
          {chunk(data?.user?.role?.widgets ?? [], 2).map((chunk, i) => {
            const isSingle = chunk.length === 1;

            return (
              <View row style={{ gap: 20, alignItems: 'flex-start' }} key={i}>
                {chunk.map((widget) => (
                  <PracticeTile
                    key={widget.ID}
                    color={widget.practiceTileColor}
                    title={widget.name}
                    description={widget.description}
                    practiceCount={widget.practiceCount}
                    testID={widget.ID}
                    icon={widget.iconName as ComponentProps<typeof PracticeTile>['icon']}
                    onPress={() => {
                      openDeeplink(widget.practiceTileDeeplink);
                    }}
                    lockedUntil={widget.locked ? widget.unlockSession?.session.ordinal : undefined}
                  />
                ))}
                {isSingle ? <View flex={1} style={{ padding: 16 }} /> : null}
              </View>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
}
